import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, FileSignature, Receipt } from "lucide-react";

// Pick an estimate (or pass estimateId directly) → creates Invoice + optional Contract
export const ConvertEstimateDialog = ({ open, onOpenChange, leadId, estimateId, onDone }) => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [selected, setSelected] = useState(estimateId || null);
  const [createContract, setCreateContract] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(estimateId || null);
    if (!estimateId && leadId) {
      axios.get(`${API}/estimates`, { params: { lead_id: leadId } })
        .then(r => setEstimates((r.data || []).filter(e => e.lead_id === leadId)))
        .catch(() => toast.error("Failed to load estimates"));
    }
  }, [open, leadId, estimateId]);

  const convert = async () => {
    if (!selected) return toast.error("Pick an estimate first");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/estimates/${selected}/convert-to-invoice`, { create_contract: createContract });
      toast.success(r.data.already_existed ? "Invoice already existed — opened it" : `Invoice ${r.data.invoice.invoice_number} created${r.data.contract ? ` + contract ${r.data.contract.contract_number}` : ""}`);
      onOpenChange(false);
      onDone?.(r.data);
      navigate(r.data.contract ? `/contracts/${r.data.contract.contract_id}` : `/invoices/${r.data.invoice.invoice_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="convert-estimate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-indigo-600" /> Convert to Invoice</DialogTitle>
          <DialogDescription>Creates a numbered invoice (work order) from the estimate. Optionally draft the contract at the same time with the payment schedule filled in.</DialogDescription>
        </DialogHeader>
        {!estimateId && (
          <div className="max-h-64 overflow-y-auto divide-y border rounded-md">
            {estimates.length === 0 && <p className="text-sm text-slate-500 p-4 text-center">No estimates for this customer yet.</p>}
            {estimates.map(e => (
              <button key={e.estimate_id} type="button" onClick={() => setSelected(e.estimate_id)}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 ${selected === e.estimate_id ? "bg-indigo-50" : ""}`} data-testid={`pick-estimate-${e.estimate_id}`}>
                <div><p className="text-sm font-medium">{e.estimate_number} <span className="text-slate-500 font-normal">· {e.category}</span></p><p className="text-xs text-slate-500">${Number(e.total || 0).toLocaleString()}</p></div>
                <Badge variant="outline" className={e.status === "accepted" ? "border-green-300 text-green-700" : ""}>{e.status}</Badge>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between border rounded-md p-3">
          <div className="flex items-center gap-2"><FileSignature className="w-4 h-4 text-indigo-600" /><div><p className="text-sm font-medium">Also create contract</p><p className="text-xs text-slate-500">Pre-filled agreement, ready to edit & send for e-signature</p></div></div>
          <Switch checked={createContract} onCheckedChange={setCreateContract} data-testid="convert-create-contract" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={convert} disabled={busy || !selected} data-testid="convert-submit">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create {createContract ? "Invoice + Contract" : "Invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
